import RydHeaderDialogBox from '.././widgets/RydHeaderDialogBox';
import React, { useEffect } from 'react';
import { normalizeClassName } from '../../render/renderUtils';
import { InstrumentName } from 'soundfont-player';
import {
    listSoundfondInstruments,
    listVirtualInstruments,
    RydExternalMidiInstrument,
    RydInstrument,
    RydInstrumentFactory,
    RydSFInstrument,
    RydVirtualInstrument
} from '../../model/instrument';
import { AudioSink, listAudioSinks } from '../../audio/routing';
import { MidiDeviceSelectionDialog } from '../midi/MidiDeviceSelectionDialog';
import '../midi/MidiDeviceSelectionDialog.css';
import '../widgets/RydRollHeaderGroup.css';
import './InstrumentSelectionDialog.css';
import './IOSelectionDialogCommons.css';
import { useAudioContextStore } from '../../stores/AudioContextStore';
import { TrackOutputType } from '../../model/tracks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { mapToInstrument } from '../../audio/instruments/utils';
import { keybindingsGlobalSuspend } from '../../keybindings/dispatcher';
import { HeaderDialogSearchBox } from './HeaderDialogSearchBox';


export function SoundfontInstrumentSelectionDialog({
    selectedInstrument,
    setInstrument,
    collapsedClass,
    standalone = false // defines wheter this component is in use with a outputtype selection (flat/joined borders) or standalone (normal borders)
}: {
    selectedInstrument?: InstrumentName,
    setInstrument: (instrument?: InstrumentName) => void,
    collapsedClass?: string,
    standalone?: boolean
}) {

    const [collapsed, setCollapsed] = React.useState(true);
    const [search, setSearch] = React.useState('');

    if (collapsed) {
        return (
            <button
                onClick={() => setCollapsed(false)}
                className={normalizeClassName([!standalone ? 'flat-left' : ''].concat(collapsedClass ? [collapsedClass] : []))}
            >
                {selectedInstrument ? selectedInstrument : '---'}
            </button>
        );
    }

    return (<RydHeaderDialogBox closeCB={() => { setCollapsed(true) }}>
        <>
            <HeaderDialogSearchBox setSearch={setSearch} />
            <ul className={normalizeClassName(['midi-device-panel--list'])}>
                <li key={'__default__'}
                    className={normalizeClassName(['ryd-button-option'].concat(!selectedInstrument ? ['ryd-button-option--active'] : []))}
                    onClick={() => {
                        setInstrument(undefined);
                        setCollapsed(true);
                    }}>
                    ---
                </li>
                {listSoundfondInstruments()
                    .filter((vi) => search.length === 0 || vi.name.toLowerCase().includes(search))
                    .map((instrument: RydInstrument, idx: number) => {
                        const classes = ['ryd-button-option'];
                        if (instrument.id === selectedInstrument) {
                            classes.push('ryd-button-option--active');
                        }
                        return (
                            <li key={idx}
                                className={normalizeClassName(classes)}
                                onClick={() => {
                                    setInstrument?.(instrument.id as InstrumentName);
                                    setCollapsed(true);
                                }}>
                                {instrument.name}
                            </li>
                        );
                    })}
            </ul>
        </>
    </RydHeaderDialogBox>);
}

const outputTypes: TrackOutputType[] = ['VSYNTH', 'SF2', 'EXTMIDI'];
const outputDisplayLabels: Record<TrackOutputType, string> = {
    VSYNTH: 'Synth',
    SF2: 'SF2',
    EXTMIDI: 'MIDI'
};

export function TrackOutputSelectionDialog({
    trackOutputType,
    setTrackOutput,
    collapsedClass,
    standalone = false // defines wheter this component is in use with instrument selection (flat/joined borders) or standalone (normal borders)
}: {
    trackOutputType?: TrackOutputType,
    setTrackOutput: (outputType?: TrackOutputType) => void,
    collapsedClass?: string,
    standalone?: boolean
}) {

    const [collapsed, setCollapsed] = React.useState(true);

    if (collapsed) {
        return (
            <button className={normalizeClassName(['ryd-combo-button-menu--left', !standalone ? 'flat-right' : ''])}
                onClick={() => setCollapsed(false)}
            >
                {trackOutputType ? outputDisplayLabels[trackOutputType] : '---'}:
            </button>
        );
    }

    return (<RydHeaderDialogBox closeCB={() => { setCollapsed(true) }}>
        <ul className={normalizeClassName(['midi-device-panel--list'])}>
            <li key={'__default__'}
                className={normalizeClassName(['ryd-button-option'].concat(!trackOutputType ? ['ryd-button-option--active'] : []))}
                onClick={() => setTrackOutput(undefined)}>
                ---
            </li>
            {outputTypes.map((outputType, idx) => {
                const classes = ['ryd-button-option'];
                if (outputType === trackOutputType) {
                    classes.push('ryd-button-option--active');
                }
                return (
                    <li key={idx}
                        className={normalizeClassName(classes)}
                        onClick={() => {
                            setTrackOutput?.(outputType);
                            setCollapsed(true);
                        }}
                    >
                        {outputDisplayLabels[outputType]}
                    </li>
                );
            })}
        </ul>
    </RydHeaderDialogBox>);
}

export function VirtualInstrumentSelectionDialog({
    selected,
    setSelected,
    collapsedClass,
    standalone = false // defines wheter this component is in use with a outputtype selection (flat/joined borders) or standalone (normal borders)
}: {
    selected?: RydInstrumentFactory,
    setSelected: (v?: RydInstrumentFactory) => void,
    collapsedClass? : string,
    standalone?: boolean
}) {

    const [collapsed, setCollapsed] = React.useState(true);
    const audioContextStore = useAudioContextStore();

    if (collapsed) {
        return (
            <button
                onClick={() => setCollapsed(false)}
                className={normalizeClassName([!standalone ? 'flat-left' : ''].concat(collapsedClass ? [collapsedClass] : []))}
            >
                {selected ? selected.name : '---'}
            </button>
        );
    }

    if (!audioContextStore.audioContext) {
        return (
            <RydHeaderDialogBox closeCB={() => { setCollapsed(true) }}>
                <div>Audio context not initialized.</div>
            </RydHeaderDialogBox>
        );
    }

    return (<RydHeaderDialogBox closeCB={() => { setCollapsed(true) }}>
        <ul className={normalizeClassName(['midi-device-panel--list'])}>
            <li key={'__default__'}
                className={normalizeClassName(['ryd-button-option'].concat(!selected ? ['ryd-button-option--active'] : []))}
                onClick={() => setSelected(undefined)}>
                ---
            </li>
            {listVirtualInstruments()
                .map((outputType, idx) => {
                    const classes = ['ryd-button-option'];
                    if (outputType.id === selected?.id) {
                        classes.push('ryd-button-option--active');
                    }
                    return (
                        <li key={idx}
                            className={normalizeClassName(classes)}
                            onClick={() => {
                                setSelected(outputType);
                                setCollapsed(true);
                            }}
                        >
                            {outputType.name}
                        </li>
                    );
                })}
        </ul>
    </RydHeaderDialogBox>);
}

function renderForSelectedOutputType({
    midiSink,
    setMidiSink,
    trackOutputType,
    selectedInstrument,
    setSelected,
    virtualInstrument,
    setVirtualInstrument,
    collapsedClass
}: {
    midiSink?: AudioSink,
    setMidiSink: (source: AudioSink) => void
    trackOutputType: TrackOutputType | undefined,
    selectedInstrument?: InstrumentName,
    setSelected: (instrument: InstrumentName) => void,
    virtualInstrument?: RydInstrumentFactory,
    setVirtualInstrument: (instrument: RydInstrumentFactory) => void,
    collapsedClass?: string
}) {
    switch (trackOutputType) {
        case 'VSYNTH':
            return <VirtualInstrumentSelectionDialog
                setSelected={setVirtualInstrument}
                selected={virtualInstrument}
                collapsedClass={collapsedClass}
            />;
        case 'SF2':
            return <SoundfontInstrumentSelectionDialog
                selectedInstrument={selectedInstrument}
                setInstrument={setSelected}
                collapsedClass={collapsedClass}
            />;
        case 'EXTMIDI':
            return <MidiDeviceSelectionDialog<AudioSink>
                selected={midiSink}
                listSource={listAudioSinks}
                setCB={setMidiSink}
                collapsedClass={collapsedClass}
            />;
        default:
            return null;
    }
}

export function RydTrackInstrumentSelectionDialog({
    trackInstrument,
    setTrackInstrument,
    standalone = false // defines wheter this component is in use with a outputtype selection (flat/joined borders) or standalone (normal borders)
}: {
    trackInstrument?: RydInstrument,
    setTrackInstrument?: (instrument?: RydInstrument) => void,
    standalone?: boolean
}) {

    const [trackOutputType, setTrackOutputType] = React.useState<TrackOutputType>();
    const [sfInstrument, setSFInstrument] = React.useState<InstrumentName>();
    const [vInstrument, setVirtualInstrument] = React.useState<RydInstrumentFactory>();
    const [midiSink, setMidiSink] = React.useState<AudioSink>(trackInstrument instanceof RydExternalMidiInstrument ? trackInstrument.midiOutput : undefined);
    const audioContextStore = useAudioContextStore();

    useEffect(() => {
        if (!trackOutputType) return;

        const i = mapToInstrument({
            trackOutputType,
            vInstrument,
            sfInstrument,
            midiSink,
            // trackInstrument,
            audioContext: audioContextStore.audioContext!
        });
        if (i) {
            setTrackInstrument?.(i);
        }
    }, [trackOutputType, midiSink, sfInstrument, vInstrument]);

    useEffect(() => {
        if (trackInstrument) {
            setTrackOutputType(trackInstrument.getOutputType());
            if (trackInstrument instanceof RydSFInstrument) {
                setSFInstrument(trackInstrument.instrumentName);
            } else if (trackInstrument instanceof RydVirtualInstrument) {
                setVirtualInstrument({id: trackInstrument.id, name: trackInstrument.name, } as RydInstrumentFactory);
            } else if (trackInstrument instanceof RydExternalMidiInstrument) {
                setMidiSink(trackInstrument.midiSink);
            }
        }
    }, []);

    const outputSpecific = renderForSelectedOutputType({
        midiSink: midiSink,
        trackOutputType: trackOutputType,
        selectedInstrument: sfInstrument,
        setSelected: setSFInstrument,
        setMidiSink: setMidiSink,
        virtualInstrument: vInstrument,
        setVirtualInstrument: setVirtualInstrument,
        // FIXME: change this to extraClasses and use flat-right/left
        collapsedClass: 'flat-left'
    });

    return (<>
        <div className="ryd-instrument-selection-dialog">
            <TrackOutputSelectionDialog
                setTrackOutput={setTrackOutputType}
                trackOutputType={trackOutputType}
                collapsedClass={'ryd-combo-button-menu--left'} />
            {outputSpecific}
        </div>
    </>);
}

export function TrackOutputTypeIndicator({
    output,
}: {
    output?: RydInstrument
}) {

    if (!output?.getOutputType()) {
        return <button className="ryd-track-output-type-indicator">---</button>;
    }

    return <button
        className="ryd-track-output-type-indicator"
        title={output?.name}
    >
        {output.getOutputType()}
    </button>;
}